import React, { forwardRef, useId } from 'react';
import type {
  GroupBase,
  Props as ReactSelectProps,
  StylesConfig,
} from 'react-select';
import ReactSelect, { components, mergeStyles } from 'react-select';

import type { FlexProps } from '@/components/Flex';
import { Flex } from '@/components/Flex';
import type { InputProps } from '@/components/Input';
import { generateDataTestId } from '@/lib/tests';
import { theme } from '@/theme/getTheme';

import * as S from './Select.styles';

export type SelectOption<V = string, L = string, D = unknown> = {
  data?: D;
  label: L;
  value: V;
};

export interface SelectProps<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Partial<ReactSelectProps<Option, IsMulti, Group>>,
    Pick<InputProps, 'colorVariant' | 'borderColorVariant'> {
  flexProps?: FlexProps;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label?: string;
  phonePrefixDisplayFormat?: 'short' | 'dialCode';
  variant?: 'boxed' | 'default' | 'transparent';
}

export const Select = forwardRef<any, SelectProps>(
  (
    {
      colorVariant = 'white',
      flexProps,
      icon,
      label,
      styles,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    const customProps = { colorVariant } as Partial<SelectProps>;
    let selectedVariant;
    switch (variant) {
      case 'boxed':
        selectedVariant = S.boxedVariant;
        break;
      case 'transparent':
        selectedVariant = S.transparentVariant;
        break;
      case 'default':
      default:
        selectedVariant = S.defaultVariant;
    }

    selectedVariant = mergeStyles(
      selectedVariant,
      theme.components.select.variantStyles?.[variant] as StylesConfig<
        SelectOption,
        false,
        GroupBase<SelectOption>
      >,
    );

    // Add custom SingleValue component to trim the label when select is closed (used for phone number)
    const CustomSingleValue = (singleValueProps: any) => {
      let labelText = singleValueProps.children;
      if (
        props.phonePrefixDisplayFormat === 'short' &&
        typeof labelText === 'string'
      ) {
        const parenMatch = labelText.match(/\s*(\(\+\d+(?:-\d+)?\))\s*$/);
        if (parenMatch && labelText.length > 18) {
          const parenPart = parenMatch[1].trim();
          // Reserve 3 characters for ellipsis and 1 for a space
          const allowedFront = 18 - parenPart.length - 4;
          if (allowedFront > 0) {
            const trimmedFront = labelText.substring(0, allowedFront);
            labelText = `${trimmedFront}... ${parenPart}`;
          }
        }
      }
      if (
        props.phonePrefixDisplayFormat == 'dialCode' &&
        typeof labelText === 'string'
      ) {
        const dialCodeMatch = /\(([^()]*)\)/.exec(labelText);
        if (dialCodeMatch) {
          labelText = dialCodeMatch[1].trim();
        }
      }
      return (
        <components.SingleValue {...singleValueProps}>
          {labelText}
        </components.SingleValue>
      );
    };

    const customComponents = {
      ...props.components,
      SingleValue: CustomSingleValue,
    };

    return (
      <Flex
        alignItems='center'
        data-test-id='SelectFlex'
        {...generateDataTestId('select', props.name || props.id || '')}
        gridGap={1}
        {...flexProps}
        position='relative'
      >
        {label && (
          <S.LabelWrapper
            colorVariant={colorVariant}
            data-test-id='SelectLabelWrapper'
          >
            {label}
          </S.LabelWrapper>
        )}
        <ReactSelect
          ref={ref as any}
          aria-label={label}
          data-test-id='SelectReactSelect'
          {...customProps}
          {...props}
          components={customComponents}
          instanceId={useId()}
          styles={mergeStyles(selectedVariant, styles)}
        />
        {icon && (
          <S.IconContainer as={icon} data-test-id='SelectIconContainer' />
        )}
      </Flex>
    );
  },
);

Select.displayName = 'Select';
